/**
 * The frame every block panel shares: an accent rule, the block's name, a close
 * control, the body, and the remove action pinned to the bottom.
 *
 * The panel overlays the right edge of the canvas rather than sitting beside it
 * so opening one does not reflow the graph underneath.
 */

import { Button, Popconfirm, Tooltip } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import React, { FC, ReactNode } from 'react';
import { AOPPanelResizeHandle } from '../AOPPanelResizeHandle';

interface AOPPanelShellProps {
	title: ReactNode;
	/** The step type's accent, as a background class, drawn as a rule beside the title. */
	accentClass: string;
	children: ReactNode;
	onClose: () => void;
	/** Omitted for the Start block, which is not a step and cannot be removed. */
	onRemove?: () => void;
	removeLabel?: string;
	width: number;
	onResizePointerDown: (event: React.PointerEvent) => void;
	onResizeKeyDown: (event: React.KeyboardEvent) => void;
}

export const AOPPanelShell: FC<AOPPanelShellProps> = ({
	title,
	accentClass,
	children,
	onClose,
	onRemove,
	removeLabel,
	width,
	onResizePointerDown,
	onResizeKeyDown,
}) => (
	<div
		className="pointer-events-auto absolute bottom-0 right-0 top-0 z-20 flex flex-col border-0 border-l border-solid border-grey-4 bg-grey-1 shadow-lg"
		style={{ width }}
	>
		<AOPPanelResizeHandle onPointerDown={onResizePointerDown} onKeyDown={onResizeKeyDown} />

		<div className="flex shrink-0 items-center gap-xs border-0 border-b border-solid border-grey-4 py-s pl-l pr-m">
			<span className={`h-5 w-[3px] shrink-0 rounded-sm ${accentClass}`} />
			<div className="min-w-0 flex-1">{title}</div>
			<Button type="text" aria-label="Close panel" icon={<CloseOutlined />} onClick={onClose} />
		</div>

		<div className="flex min-h-0 flex-1 flex-col gap-m overflow-y-auto px-m py-m">{children}</div>

		{onRemove ? (
			<div className="shrink-0 border-0 border-t border-solid border-grey-4 px-m py-s">
				<Popconfirm
					title="Delete this block? Anything pointing at it will need rewiring."
					okText="Delete"
					okButtonProps={{ danger: true }}
					cancelText="Cancel"
					onConfirm={onRemove}
				>
					<Button danger block>
						{removeLabel ? removeLabel : 'Remove step'}
					</Button>
				</Popconfirm>
			</div>
		) : null}
	</div>
);

/** A labelled row in a panel body. */
export const AOPPanelField: FC<{
	label: ReactNode;
	required?: boolean;
	help?: ReactNode;
	children: ReactNode;
}> = ({ label, required, help, children }) => (
	<div className="flex flex-col gap-2xs">
		<span className="bodyregular text-character-primary">
			{required ? <span className="mr-2xs text-character-danger">*</span> : null}
			{label}
		</span>
		{children}
		{help ? <span className="footnoteregular text-character-secondary">{help}</span> : null}
	</div>
);

/**
 * A section of a panel: a heading, an optional action on the right, and a rule
 * above it separating it from what came before.
 */
export const AOPPanelSection: FC<{
	title: ReactNode;
	required?: boolean;
	action?: ReactNode;
	first?: boolean;
	/**
	 * What the section means, behind an info mark beside its title. For the
	 * sentence that is worth reading once and then never again — in the heading
	 * it would be noise on every visit, and left out it is a thing nobody can
	 * find out.
	 */
	info?: ReactNode;
	children: ReactNode;
}> = ({ title, required, action, first, info, children }) => (
	<div
		className={`flex flex-col gap-s ${
			first ? '' : 'border-0 border-t border-solid border-grey-4 pt-m'
		}`}
	>
		<div className="flex items-center gap-xs">
			<span className="bodymedium text-character-primary">
				{required ? <span className="mr-2xs text-character-danger">*</span> : null}
				{title}
			</span>
			{info ? (
				<Tooltip title={info}>
					<InfoCircleOutlined className="cursor-help text-character-secondary" />
				</Tooltip>
			) : null}
			<span className="flex-1" />
			{action}
		</div>
		{children}
	</div>
);

/**
 * A labelled row inside a section: the label sits in a gutter on the left, so
 * an outcome and where it goes read as one statement rather than two stacked
 * fields.
 */
export const AOPPanelRow: FC<{
	label: ReactNode;
	required?: boolean;
	children: ReactNode;
}> = ({ label, required, children }) => (
	<div className="flex items-start gap-s">
		<span className="footnoteregular w-[52px] shrink-0 pt-xs text-character-secondary">
			{required ? <span className="mr-2xs text-character-danger">*</span> : null}
			{label}
		</span>
		<div className="min-w-0 flex-1">{children}</div>
	</div>
);

/** A read-only value, for anything the canvas shows but does not own. */
export const AOPPanelReadOnly: FC<{ children: ReactNode }> = ({ children }) => (
	<div className="bodyregular rounded border border-solid border-grey-4 bg-grey-3 px-xs py-2xs text-character-primary">
		{children}
	</div>
);
